import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import AdminPage from '../../components/admin/AdminPage';
import StatusChip from '../../components/admin/StatusChip';
import notificationsService, {
  ScheduledNotification,
  ScheduledNotificationStatus,
} from '../../services/NotificationsService';

const STATUSES: (ScheduledNotificationStatus | '')[] = [
  '',
  'DRAFT',
  'SCHEDULED',
  'SENDING',
  'SENT',
  'FAILED',
  'CANCELLED',
];

const NotificationsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<ScheduledNotification[]>([]);
  const [statusFilter, setStatusFilter] = useState<ScheduledNotificationStatus | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await notificationsService.listScheduled(
        statusFilter ? { status: statusFilter, limit: 100 } : { limit: 100 },
      );
      setItems(response.items);
    } catch {
      setError('Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <AdminPage
      title="Notifications"
      description="Draft, schedule, and monitor push notification broadcasts."
      action={
        <Button
          variant="contained"
          component={RouterLink}
          to="/notifications/new"
        >
          New notification
        </Button>
      }
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(e: SelectChangeEvent) =>
              setStatusFilter(
                (e.target.value as ScheduledNotificationStatus) || '',
              )
            }
          >
            {STATUSES.map((status) => (
              <MenuItem key={status || 'all'} value={status}>
                {status || 'All'}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={load}>
          Refresh
        </Button>
        <Button
          variant="text"
          component={RouterLink}
          to="/notifications/analytics"
        >
          View analytics
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title (EN)</TableCell>
                <TableCell>Audience</TableCell>
                <TableCell>Scheduled for</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Sent / Failed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No notifications match the current filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/notifications/${row.id}`)}
                  >
                    <TableCell>
                      <Typography variant="body2">{row.titleEn}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {row.category}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.audienceKind}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(row.scheduledFor).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip value={row.status} />
                    </TableCell>
                    <TableCell align="right">
                      {row.successCount} / {row.failureCount}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AdminPage>
  );
};

export default NotificationsListPage;
